import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react"
import { getConsumer } from "@/lib/cable"
import type { DeviceStatusMessage, ReadingData } from "@/types/cable"
import type { SensorReading } from "@/types/device"

// Connected threshold: 10 seconds (per requirements)
const CONNECTED_THRESHOLD_MS = 10_000

// Per-device readings ring buffer: 24h * 60 min / ~1 sample/min = ~1440;
// add headroom for faster sample rates.
const READINGS_RING_CAP = 1500

export interface ReadingPoint {
  t: number // epoch ms
  sg: number | null
  temp: number | null
  tilt: number | null
  battery: number | null
}

interface DeviceStatusState {
  [deviceId: number]: string | null // last_seen_at timestamp
}

interface DeviceReadingsState {
  [deviceId: number]: SensorReading | null
}

interface DeviceHistoriesState {
  [deviceId: number]: ReadingPoint[]
}

interface DeviceStatusContextValue {
  getLastSeenAt: (deviceId: number) => string | null
  isConnected: (deviceId: number) => boolean
  updateDeviceStatus: (deviceId: number, lastSeenAt: string | null) => void
  getLatestReading: (deviceId: number) => SensorReading | null
  seedHistory: (deviceId: number, points: ReadingPoint[]) => void
  getReadingsHistory: (deviceId: number, sinceMs?: number) => ReadingPoint[]
  tick: number
}

function mergeAndCap(existing: ReadingPoint[], incoming: ReadingPoint[]): ReadingPoint[] {
  const byTimestamp = new Map<number, ReadingPoint>()
  for (const point of existing) byTimestamp.set(point.t, point)
  for (const point of incoming) byTimestamp.set(point.t, point)
  const merged = Array.from(byTimestamp.values()).sort((a, b) => a.t - b.t)
  return merged.length > READINGS_RING_CAP
    ? merged.slice(merged.length - READINGS_RING_CAP)
    : merged
}

// Ensure value is a number or null (Action Cable may send strings)
function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null
  const num = typeof value === "number" ? value : parseFloat(String(value))
  return isNaN(num) ? null : num
}

// Convert snake_case reading from API to camelCase SensorReading
function convertReading(reading: ReadingData | null | undefined): SensorReading | null {
  if (!reading) return null
  return {
    specificGravity: toNumber(reading.specific_gravity),
    temperature: toNumber(reading.temperature),
    tiltAngle: toNumber(reading.tilt_angle),
    batteryVoltage: toNumber(reading.battery_voltage),
    accelerometer: reading.accelerometer,
    pth: reading.pth,
  }
}

const DeviceStatusContext = createContext<DeviceStatusContextValue | null>(null)

interface DeviceStatusProviderProps {
  children: ReactNode
}

export function DeviceStatusProvider({ children }: DeviceStatusProviderProps) {
  const [deviceStatuses, setDeviceStatuses] = useState<DeviceStatusState>({})
  const [deviceReadings, setDeviceReadings] = useState<DeviceReadingsState>({})
  const [deviceHistories, setDeviceHistories] = useState<DeviceHistoriesState>({})

  // Force re-render every second to update "connected" status
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Subscribe to Action Cable
  useEffect(() => {
    const consumer = getConsumer()

    const subscription = consumer.subscriptions.create<DeviceStatusMessage>(
      { channel: "DevicesChannel" },
      {
        received(data: DeviceStatusMessage) {
          setDeviceStatuses((prev) => ({
            ...prev,
            [data.device_id]: data.last_seen_at,
          }))
          // Store reading data if present
          if (data.reading) {
            const converted = convertReading(data.reading)
            setDeviceReadings((prev) => ({
              ...prev,
              [data.device_id]: converted,
            }))
            if (converted) {
              const point: ReadingPoint = {
                t: data.last_seen_at ? Date.parse(data.last_seen_at) : Date.now(),
                sg: converted.specificGravity,
                temp: converted.temperature,
                tilt: converted.tiltAngle,
                battery: converted.batteryVoltage,
              }
              setDeviceHistories((prev) => ({
                ...prev,
                [data.device_id]: mergeAndCap(prev[data.device_id] ?? [], [point]),
              }))
            }
          }
        },
        connected() {
          console.log("[DevicesChannel] Connected")
        },
        disconnected() {
          console.log("[DevicesChannel] Disconnected")
        },
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const getLastSeenAt = useCallback(
    (deviceId: number) => deviceStatuses[deviceId] ?? null,
    [deviceStatuses]
  )

  const isConnected = useCallback(
    (deviceId: number) => {
      const lastSeenAt = deviceStatuses[deviceId]
      if (!lastSeenAt) return false

      const date = new Date(lastSeenAt)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()

      return diffMs < CONNECTED_THRESHOLD_MS
    },
    [deviceStatuses]
  )

  const updateDeviceStatus = useCallback(
    (deviceId: number, lastSeenAt: string | null) => {
      setDeviceStatuses((prev) => ({
        ...prev,
        [deviceId]: lastSeenAt,
      }))
    },
    []
  )

  const getLatestReading = useCallback(
    (deviceId: number) => deviceReadings[deviceId] ?? null,
    [deviceReadings]
  )

  const seedHistory = useCallback((deviceId: number, points: ReadingPoint[]) => {
    setDeviceHistories((prev) => ({
      ...prev,
      [deviceId]: mergeAndCap(prev[deviceId] ?? [], points),
    }))
  }, [])

  const getReadingsHistory = useCallback(
    (deviceId: number, sinceMs?: number) => {
      const points = deviceHistories[deviceId] ?? []
      if (sinceMs === undefined) return points
      return points.filter((p) => p.t >= sinceMs)
    },
    [deviceHistories]
  )

  const value = useMemo(
    () => ({
      getLastSeenAt,
      isConnected,
      updateDeviceStatus,
      getLatestReading,
      seedHistory,
      getReadingsHistory,
      tick,
    }),
    [
      getLastSeenAt,
      isConnected,
      updateDeviceStatus,
      getLatestReading,
      seedHistory,
      getReadingsHistory,
      tick,
    ]
  )

  return (
    <DeviceStatusContext.Provider value={value}>
      {children}
    </DeviceStatusContext.Provider>
  )
}

export function useDeviceStatus() {
  const context = useContext(DeviceStatusContext)
  if (!context) {
    throw new Error("useDeviceStatus must be used within a DeviceStatusProvider")
  }
  return context
}
